import React from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Box,
  Toolbar,
  Stack,
  StackProps,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation, useParams } from 'react-router-dom';
import HomeHeader from '../Home/components/HomeHeader';
import { TP_COLORS } from '../../constants';
import HomeFooter from '../Home/components/HomeFooter';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../components/BreadCrumb/Breadcrumbs';

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const constructBreadcrumbOptions = (
  curriculum: string | undefined,
  subject: string | undefined,
  subjectCode: string,
  subjectLevel: string | undefined
) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'link',
      title: `${curriculum}`
        .split('-')
        .map((word, index) =>
          index === 1
            ? word.toUpperCase()
            : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(' '),
      link: `${process.env.PUBLIC_URL}/${curriculum}`,
    },
    {
      type: 'link',
      title: `${subject}`
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      link: `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`,
    },
    {
      type: 'text',
      title: 'Past Papers',
      highlight: true,
    },
  ];

  return options;
};

const papersDataCambridge = [
  {
    year: 2025,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://www.examsolutions.net/wp-content/uploads/2023/06/663662-2025-specimen-paper-1.pdf',
      },
      {
        label: 'Paper 2 (Extended)',
        link:
          'https://www.examsolutions.net/wp-content/uploads/2023/06/663664-2025-specimen-paper-2.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://www.examsolutions.net/wp-content/uploads/2023/06/663666-2025-specimen-paper-3.pdf',
      },
      {
        label: 'Paper 4 (Extended)',
        link:
          'https://www.examsolutions.net/wp-content/uploads/2023/06/663668-2025-specimen-paper-4.pdf',
      },
    ],
  },
  {
    year: 2024,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2024-May-June/0580_s24_qp_11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2024-May-June/0580_s24_qp_21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2024-May-June/0580_s24_qp_31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2024-May-June/0580_s24_qp_41.pdf',
      },
    ],
  },
  {
    year: 2023,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2023-May-June/0580_s23_qp_11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2023-May-June/0580_s23_qp_21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2023-May-June/0580_s23_qp_31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2023-May-June/0580_s23_qp_41.pdf',
      },
    ],
  },
  {
    year: 2022,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://www.cambridgeinternational.org/Images/569923-june-2022-question-paper-11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://www.cambridgeinternational.org/Images/569924-june-2022-question-paper-21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://www.cambridgeinternational.org/Images/569925-june-2022-question-paper-31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://www.cambridgeinternational.org/Images/671439-june-2022-question-paper-41.pdf',
      },
    ],
  },
  {
    year: 2021,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2021-May-June/0580_s21_qp_11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2021-May-June/0580_s21_qp_21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2021-May-June/0580_s21_qp_31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2021-Oct-Nov/0580_w21_qp_41.pdf',
      },
    ],
  },
  {
    year: 2020,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2020-Oct-Nov/0580_w20_qp_11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2020-Oct-Nov/0580_w20_qp_21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2020-Oct-Nov/0580_w20_qp_31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2020-Oct-Nov/0580_w20_qp_41.pdf',
      },
    ],
  },
  {
    year: 2019,
    papers: [
      {
        label: 'Paper 1 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2019-Oct-Nov/0580_w19_qp_11.pdf',
      },
      {
        label: 'Paper 2 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2019-Oct-Nov/0580_w19_qp_21.pdf',
      },
      {
        label: 'Paper 3 (Core)',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2019-Oct-Nov/0580_w19_qp_31.pdf',
      },
      {
        label: 'Paper 4 (Extended',
        link:
          'https://pastpapers.co/cie/IGCSE/Mathematics-0580/2019-Oct-Nov/0580_w19_qp_41.pdf',
      },
    ],
  },
];

const papersDataEdexcel = [
  {
    year: 2023,
    papers: [
      {
        label: 'Paper 1F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-1f-que-20230111.pdf',
      },
      {
        label: 'Paper 1FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-1fr-que-20230111.pdf',
      },
      {
        label: 'Paper 2F',
        link:
          'https://www.examhttps://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-2f-que-20230118.pdf',
      },
      {
        label: 'Paper 2FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-2fr-que-20230118.pdf',
      },
      {
        label: 'Paper 1H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-1h-que-20230111.pdf',
      },
      {
        label: 'Paper 1HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-1hr-que-20230111.pdf',
      },
      {
        label: 'Paper 2H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-2h-que-20230118.pdf',
      },
      {
        label: 'Paper 2HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4ma1-2hr-que-20230118.pdf',
      },
    ],
  },
  {
    year: 2022,
    papers: [
      {
        label: 'Paper 1F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1F_que_20220111.pdf',
      },
      {
        label: 'Paper 1FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1FR_que_20220111.pdf',
      },
      {
        label: 'Paper 2F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2F_que_20220118.pdf',
      },
      {
        label: 'Paper 2FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2FR_que_20220118.pdf',
      },
      {
        label: 'Paper 1H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1H_que_20220111.pdf',
      },
      {
        label: 'Paper 1HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1HR_que_20220111.pdf',
      },
      {
        label: 'Paper 2H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2H_que_20220118.pdf',
      },
      {
        label: 'Paper 2HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2HR_que_20220118.pdf',
      },
    ],
  },
  {
    year: 2021,
    papers: [
      {
        label: 'Paper 1F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/Exam-materials/4MA1_1F_que_20210428.pdf',
      },
      {
        label: 'Paper 1FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1FR_que_20210304.pdf',
      },
      {
        label: 'Paper 2F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/Exam-materials/4MA1_2F_que_20210430.pdf',
      },
      {
        label: 'Paper 2FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2FR_que_20210304.pdf',
      },
      {
        label: 'Paper 1H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/Exam-materials/4MA1_1H_que_20210420.pdf',
      },
      {
        label: 'Paper 1HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1HR_que_20210304.pdf',
      },
      {
        label: 'Paper 2H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/Exam-materials/4MA1_2H_que_20210430.pdf',
      },
      {
        label: 'Paper 2HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2HR_que_20210304.pdf',
      },
    ],
  },
  {
    year: 2020,
    papers: [
      {
        label: 'Paper 1F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1F_que_20200305.pdf',
      },
      {
        label: 'Paper 1FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1FR_que_20200305.pdf',
      },
      {
        label: 'Paper 2F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2F_que_20200305.pdf',
      },
      {
        label: 'Paper 2FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2FR_que_20200305.pdf',
      },
      {
        label: 'Paper 1H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1H_que_20200305.pdf',
      },
      {
        label: 'Paper 1HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1HR_que_20200305.pdf',
      },
      {
        label: 'Paper 2H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2H_que_20200305.pdf',
      },
      {
        label: 'Paper 2HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2HR_que_20200305-.pdf',
      },
    ],
  },
  {
    year: 2019,
    papers: [
      {
        label: 'Paper 1F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1F_que_20190110.pdf',
      },
      {
        label: 'Paper 1FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1FR_que_20190110.pdf',
      },
      {
        label: 'Paper 2F',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2F_que_20190116.pdf',
      },
      {
        label: 'Paper 2FR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2FR_que_20190116.pdf',
      },
      {
        label: 'Paper 1H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1H_que_20190110.pdf',
      },
      {
        label: 'Paper 1HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_1HR_que_20190110.pdf',
      },
      {
        label: 'Paper 2H',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2H_que_20190116.pdf',
      },
      {
        label: 'Paper 2HR',
        link:
          'https://qualifications.pearson.com/content/dam/pdf/International%20GCSE/Mathematics%20A/2016/exam-materials/4MA1_2HR_que_20190116.pdf',
      },
    ],
  },
];

const FeatureCard = ({ title, description, imageSrc, bgColor, titleColor }) => {
  return (
    <Card
      style={{ backgroundColor: bgColor, height: '100%', borderRadius: 10 }}
    >
      <CardContent>
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <img src={imageSrc} alt={title} style={{ width: 35, height: 35 }} />
        </Box>
        <Typography
          sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: titleColor }}
          gutterBottom
          align="center"
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', pt: 1 }} align="center">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface LocationState {
  subjectCode: string;
  subjectLevel: string;
}

const PastPapersPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  const subjectCode = state?.subjectCode;
  const subjectLevel = state?.subjectLevel;
  const params = useParams();
  const curriculum = params.curriculum?.trim();
  const subject = params.subject?.trim();
  const breadcrumbOptions = constructBreadcrumbOptions(
    curriculum,
    subject,
    subjectCode,
    subjectLevel
  );
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  let papersData;
  if (curriculum === 'cambridge-igcse') {
    papersData = papersDataCambridge;
  } else if (curriculum === 'edexcel-igcse') {
    papersData = papersDataEdexcel;
  } else {
    papersData = [];
  }

  return (
    <>
      <HomeHeader />
      <Toolbar />
      <Container maxWidth="lg" sx={{ mt: '1%', mb: '4%' }}>
        <Stack {...topStackProps}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor={TP_COLORS.gray.light}
            showDropDown={belowMd}
            highlightColor="#3498DB"
          />
        </Stack>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          style={{
            marginTop: '10px',
            fontWeight: 'bold',
            marginBottom: '20px',
          }}
        >
          <span style={{ color: TP_COLORS.blue.dark }}>
            Ace Your Exams with
          </span>
          <span style={{ color: TP_COLORS.tutopiyaGreenMain.darken }}>
            {' '}
            Expert-Curated Past Papers
          </span>
        </Typography>
        <Typography
          variant="body1"
          align="center"
          gutterBottom
          style={{ marginBottom: '40px', fontSize: '15px' }}
        >
          Practice makes perfect, and our curated collection of IGCSE past
          papers will help you master every subject. Whether it’s Mathematics,
          Physics, Chemistry, Biology, or English, you’ll find IGCSE Exam Papers
          designed to match the official syllabus.
        </Typography>

        {/* Grid of years and papers */}
        <Grid container spacing={3}>
          {papersData.map((yearData) => (
            <Grid item xs={12} sm={6} md={3} key={yearData.year}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ padding: 0 }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      backgroundColor: TP_COLORS.blue.medium,
                      color: '#fff',
                      textAlign: 'center',
                      padding: 1,
                      fontWeight: 'bold',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4,
                    }}
                  >
                    {yearData.year}
                  </Typography>
                  <Grid
                    container
                    direction="column"
                    spacing={1}
                    sx={{ padding: '10px 10px 0 10px' }}
                  >
                    {yearData.papers.map((paper, index) => (
                      <Grid
                        item
                        container
                        key={index}
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          borderBottom:
                            index < yearData.papers.length - 1
                              ? '1px solid #e0e0e0'
                              : 'none',
                          paddingBottom:
                            index < yearData.papers.length - 1 ? 1 : 0,
                        }}
                      >
                        <Typography variant="body1">{paper.label}</Typography>
                        <IconButton
                          component="a"
                          href={paper.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography
          variant="h5"
          align="center"
          gutterBottom
          style={{ marginTop: '50px', fontWeight: 'bold' }}
        >
          <span style={{ color: TP_COLORS.blue.dark }}>Why Use IGCSE</span>
          <span style={{ color: TP_COLORS.tutopiyaGreenMain.darken }}>
            {' '}
            Past Papers?
          </span>
        </Typography>
        <Grid
          container
          spacing={4}
          justifyContent="center"
          style={{ marginTop: '20px' }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Real Exam Practice"
              description="Familiarize yourself with actual IGCSE exam formats, question styles, and mark schemes."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/exam.png`}
              bgColor="#3498DB1A"
              titleColor="#3498DB"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="All Major Subjects"
              description="Access past papers for IGCSE Maths, Sciences, Languages, and Humanities."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/books.png`}
              bgColor="#22C55E1A"
              titleColor="#22C55E"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Detailed Solutions"
              description="Benefit from expert solutions that help you learn from mistakes and improve your exam strategy."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/tips.png`}
              bgColor="#FFAB001A"
              titleColor="#FFAB00"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Time Management Skills"
              description="Practice under timed conditions to perfect your time management during exams."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/timer.png`}
              bgColor="#7A41001A"
              titleColor="#7A4100"
            />
          </Grid>
        </Grid>
      </Container>
      <HomeFooter />
    </>
  );
};

export default PastPapersPage;
